import React, { useCallback } from 'react'
import type { EventInput, EventMountArg } from '@fullcalendar/core'
import { FullCalendar } from '@ztechapps/webui.date'
import { useUser } from '@ztechapps/common.hook'
import { Stack } from 'react-bootstrap'
import { Box } from '@ztechapps/webui.util'

type TaskCalendarProps = {
  events?: EventInput[]
  onDateChange?: (dateInfo: { currentStart: Date; currentEnd: Date }) => void
  onEventDoubleClick?: (taskId: string, event: MouseEvent) => void
  initialView?: string
  height?: string
  canEdit?: boolean
}

export const TaskCalendar = ({
  events,
  onDateChange,
  onEventDoubleClick,
  initialView = 'dayGridMonth',
  height = 'calc(100vh - 280px)',
  canEdit = true,
}: TaskCalendarProps) => {
  const { user } = useUser()

  const handleEventDidMount = useCallback(
    (info: EventMountArg) => {
      const el = info.el as HTMLElement & { _dblclickHandler?: (e: MouseEvent) => void }
      el.style.cursor = 'pointer'
      if (!onEventDoubleClick) return
      const handler = (e: MouseEvent) => {
        const taskId = info.event.extendedProps?.taskId ?? info.event.id
        if (taskId) {
          onEventDoubleClick(taskId, e)
        }
      }
      el.addEventListener('dblclick', handler)
      el._dblclickHandler = handler
    },
    [onEventDoubleClick]
  )

  const handleEventWillUnmount = useCallback((info: EventMountArg) => {
    const el = info.el as HTMLElement & { _dblclickHandler?: (e: MouseEvent) => void }
    if (el._dblclickHandler) {
      el.removeEventListener('dblclick', el._dblclickHandler)
      delete el._dblclickHandler
    }
  }, [])

  return (
    <Box>
      <Stack gap={3}>
        {/* canEdit must gate any eventDrop/eventResize added here */}
        <FullCalendar
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'timeGridDay,timeGridWeek,dayGridMonth,multiMonthYear',
          }}
          editable={canEdit}
          eventStartEditable={canEdit}
          eventDurationEditable={canEdit}
          {...{
            initialView,
            events: events as any,
            setCalendarDate: onDateChange,
            timeZone: user?.timeZone,
            height,
            views: {
              timeGridDay: { buttonText: 'day' },
              listWeek: { buttonText: 'week' },
              listMonth: { buttonText: 'month' },
              multiMonthYear: { buttonText: 'year' },
            },
            defaultAllDay: false,
            slotEventOverlap: false,
            eventShortHeight: 60,
            navLinks: true,
            eventDidMount: handleEventDidMount,
            eventWillUnmount: handleEventWillUnmount,
          }}
        />
      </Stack>
    </Box>
  )
}
